import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';
import particularesApiService from '../services/ParticularesApiService';
import { downloadAlumnoCalendarPdf } from '../utils/particularCalendarPdf';

// Grilla del calendario del alumno: el horario semanal de sus cursos y las clases que reservó con sus docentes.
// Pide al back el rango visible, así se ve cualquier semana o mes al que navegue.

const DEFAULT_CURSO_COLOR = '#475569';
const getValue = (source, lower, upper) => source?.[lower] ?? source?.[upper];
const getList = (source, lower, upper) => {
  const value = getValue(source, lower, upper);
  return Array.isArray(value) ? value : [];
};
const pad = (value) => String(value).padStart(2, '0');
const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const isValidColor = (color) => /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(String(color || '').trim());

const ParticularAlumnoCalendarGrid = ({ refreshKey = 0, normalizeSlot, onClassClick, alumnoNombre = '', institucionNombre = '' }) => {
  const [range, setRange] = useState(null);
  // Vista visible (título, tipo y fechas) para exportar exactamente lo que el alumno está mirando.
  const [vista, setVista] = useState(null);
  const [data, setData] = useState({ asignadas: [], horariosCursos: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const initialView = typeof window !== 'undefined' && window.innerWidth < 720 ? 'timeGridDay' : 'timeGridWeek';

  const load = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const response = await particularesApiService.getAlumnoCalendario(range);
      setData({
        asignadas: getList(response, 'asignadas', 'Asignadas').map(normalizeSlot),
        horariosCursos: getList(response, 'horariosCursos', 'HorariosCursos')
      });
      setError('');
    } catch (err) {
      setError(err.message || 'No pudimos cargar el calendario.');
    } finally {
      setLoading(false);
    }
  }, [normalizeSlot, range]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const events = useMemo(() => [
    ...data.horariosCursos.map((item, index) => {
      const color = isValidColor(getValue(item, 'color', 'Color')) ? getValue(item, 'color', 'Color') : DEFAULT_CURSO_COLOR;
      return {
        id: `curso-${getValue(item, 'idCursoParticular', 'IdCursoParticular')}-${index}`,
        title: getValue(item, 'cursoNombre', 'CursoNombre') || 'Curso',
        start: getValue(item, 'fechaInicioLocal', 'FechaInicioLocal'),
        end: getValue(item, 'fechaFinLocal', 'FechaFinLocal'),
        backgroundColor: color,
        borderColor: color,
        textColor: '#ffffff',
        classNames: ['sp-cal-curso'],
        extendedProps: { kind: 'curso' }
      };
    }),
    ...data.asignadas.map((slot) => ({
      id: `clase-${slot.id}`,
      title: `Clase con ${slot.profesorNombre}`,
      start: slot.fechaInicioLocal,
      end: slot.fechaFinLocal,
      backgroundColor: '#ffffff',
      borderColor: slot.profesorColor,
      textColor: '#0f172a',
      classNames: ['sp-cal-clase'],
      extendedProps: { kind: 'clase', slot }
    }))
  ], [data]);

  const handleDownloadPdf = () => {
    if (!vista) return;
    downloadAlumnoCalendarPdf({
      items: events.map((event) => ({
        title: event.title,
        start: event.start,
        end: event.end,
        kind: event.extendedProps.kind,
        color: event.extendedProps.kind === 'curso' ? event.backgroundColor : event.borderColor,
        detalle: event.extendedProps.kind === 'clase'
          ? (event.extendedProps.slot?.notas || '')
          : ''
      })),
      desde: vista.start,
      hasta: vista.end,
      periodo: vista.title,
      alumnoNombre,
      institucionNombre,
      // En la vista mensual se listan solo los días con actividad.
      incluirDiasVacios: vista.type !== 'dayGridMonth'
    });
  };

  return (
    <div className="sp-calendar-grid-wrap">
      <div className="sp-calendar-legend">
        <span><i className="legend-curso" aria-hidden="true" /> Horario de tus cursos</span>
        <span><i className="legend-clase" aria-hidden="true" /> Clases que reservaste</span>
        {loading && <span className="sp-calendar-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Actualizando</span>}
        <button
          type="button"
          className="btn-secondary sp-calendar-pdf"
          onClick={handleDownloadPdf}
          disabled={!vista || loading}
          title="Descargar en PDF lo que estás viendo (día, semana o mes)"
        >
          <i className="fas fa-file-pdf" aria-hidden="true" />
          Descargar PDF
        </button>
      </div>
      {error && <p className="sp-cursos-error" role="alert">{error}</p>}
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin]}
        locale={esLocale}
        initialView={initialView}
        headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
        buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
        allDaySlot={false}
        nowIndicator
        slotMinTime="07:00:00"
        slotMaxTime="23:00:00"
        height="auto"
        events={events}
        datesSet={(info) => {
          setRange({ from: toDateKey(info.start), to: toDateKey(info.end) });
          setVista({ title: info.view.title, type: info.view.type, start: info.start, end: info.end });
        }}
        eventClick={(info) => {
          if (info.event.extendedProps.kind === 'clase') onClassClick?.(info.event.extendedProps.slot);
        }}
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
      />
    </div>
  );
};

export default ParticularAlumnoCalendarGrid;
